





































































































import Vue from "vue";
import Component from "vue-class-component";
import store from "../core/store";
import { SettingsService } from "../core/services/configService";
import { ProgramPackTexts } from "../bookings/programPackTexts";
import { GuidelineApi, DayDone } from "../components/guidelines/guidelineApi";
import ProgramPage from "../components/programs/ProgramPage.vue";
import ProgramMedia from "../components/programs/ProgramMedia.vue";
import CzCard from "../components/cz/CzCard.vue";
import CzChip from "../components/cz/CzChip.vue";
import CzTitle from "../components/cz/CzTitle.vue";
import CzNotice from "../components/cz/CzNotice.vue";
import CzPlanRow from "../components/cz/CzPlanRow.vue";
import CzIcon from "../components/cz/CzIcon.vue";
import CzLink from "../components/cz/CzLink.vue";
import GuidelineSessionContent from "../components/guidelines/GuidelineSessionContent.vue";
import CzButton from "../components/cz/CzButton.vue";

// The guideline on one page: its week with the days that can still be recorded, the professional's note, "Stop and
// let us know if", the sessions with the next one open and the bar to mark today. With ?preview the admin sees the
// template as the customer will, and nothing is recorded.
@Component({
  watch: {
    "$route.query.session"() {
      window.scrollTo(0, 0);
    },
  },
  components: { ProgramPage, ProgramMedia, CzCard, CzChip, CzTitle, CzNotice, CzPlanRow, CzIcon, CzLink, CzButton, GuidelineSessionContent },
})
export default class GuidelinePage extends Vue {
  d: any = null;
  loaded: boolean = false;
  weekOffset: number = 0;
  busy: boolean = false;
  bodyOpen: boolean = false;
  error: string = "";
  failed: (() => Promise<void>) | null = null;

  get previewToken(): string {
    return (this.$route.query.preview as string) || "";
  }

  get backTo(): any {
    if (this.d && this.openSession) {
      const query = Object.assign({}, this.$route.query);
      delete query.session;
      return { path: this.$route.path, query: query };
    }
    if (this.previewToken) return "";
    return (this.$route.query.back as string) || "/today";
  }

  get backLabel(): string {
    if (this.d && this.openSession) return this.d.name;
    const back = (this.$route.query.back as string) || "";
    if (back.startsWith("/customer-programs")) return this.$t("Your program").toString();
    return back && back !== "/today" ? this.$t("Back").toString() : this.$t("Today").toString();
  }

  get emptyText(): string {
    return this.loaded && !this.d ? this.$t("This guideline is not available").toString() : "";
  }

  get today(): string {
    return GuidelineApi.day(this.d.today);
  }

  get facts(): string {
    const bits: string[] = [];
    if (this.d.sessions.length > 1) bits.push(this.$t("One session per day, taking turns").toString());
    if (this.d.estimatedMinutes) bits.push(this.$t("about {0} min").toString().replace("{0}", this.d.estimatedMinutes));
    if (this.d.row) {
      bits.push(this.timesPerWeek(this.d.row.timesPerWeek));
    }
    if (this.d.endDate) bits.push(this.$t("until {0}").toString().replace("{0}", ProgramPackTexts.date(GuidelineApi.addDays(GuidelineApi.day(this.d.endDate), -1) + "T12:00:00", false)));
    return bits.join(" · ");
  }

  get monday(): string {
    return GuidelineApi.addDays(GuidelineApi.mondayOf(this.today), 7 * this.weekOffset);
  }

  get canGoBack(): boolean {
    return this.monday > GuidelineApi.mondayOf(GuidelineApi.day(this.d.startDate));
  }

  get weekLabel(): string {
    return this.$t("Week of {0}").toString().replace("{0}", ProgramPackTexts.date(this.monday + "T12:00:00", false));
  }

  get weekSummary(): string {
    const week = this.d.weeks.find((w: any) => GuidelineApi.day(w.monday) === this.monday);
    if (!week) return "";
    if (week.closed) return this.$t("closed").toString();
    return this.$t("{0} of {1} days").toString().replace("{0}", String(Math.min(week.done, week.target))).replace("{1}", week.target);
  }

  get strip(): any[] {
    const name = new Intl.DateTimeFormat(ProgramPackTexts.culture(), { weekday: "long", day: "numeric" });
    const short = new Intl.DateTimeFormat(ProgramPackTexts.culture(), { weekday: "narrow" });
    const from = GuidelineApi.day(this.d.recordFrom);
    const to = GuidelineApi.day(this.d.recordTo);
    return [0, 1, 2, 3, 4, 5, 6].map((i) => {
      const date = GuidelineApi.addDays(this.monday, i);
      const record = this.d.days.find((x: any) => GuidelineApi.day(x.date) === date);
      const status = record ? record.status : null;
      const label = name.format(new Date(date + "T12:00:00"));
      const state = status === 0 ? this.$t("done").toString() : status === 1 ? this.$t("you could not").toString() : this.$t("not recorded").toString();
      return {
        date: date,
        isToday: date === this.today,
        name: date === this.today ? this.$t("Today").toString() : short.format(new Date(date + "T12:00:00")) + " " + Number(date.substring(8)),
        status: status,
        letter: status === 0 ? "✓" : status === 1 ? "–" : "",
        editable: date < this.today && date >= from && date <= to,
        aria: label + ", " + state,
      };
    });
  }

  get canRecordPast(): boolean {
    return this.strip.some((day: any) => day.editable);
  }

  get canRecordToday(): boolean {
    return !this.previewToken && !!this.d && GuidelineApi.day(this.d.recordTo) === this.today && GuidelineApi.day(this.d.recordFrom) <= this.today;
  }

  get todayStatus(): number | null {
    const record = this.d.days.find((x: any) => GuidelineApi.day(x.date) === this.today);
    return record ? record.status : null;
  }

  get bodyLong(): boolean {
    return (this.d.body || "").replace(/<[^>]*>/g, "").length > 160;
  }

  get todaySession(): any {
    if (this.todayStatus === 0) {
      const record = this.d.days.find((x: any) => GuidelineApi.day(x.date) === this.today);
      return this.d.sessions.find((x: any) => x.title === record.sessionTitle) || null;
    }
    return this.todayStatus === null ? this.nextSession : null;
  }

  get otherSessions(): any[] {
    return this.d.sessions.filter((x: any) => x !== this.todaySession);
  }

  get nextSession(): any {
    return this.d.sessions.find((s: any) => s.isNext) || null;
  }

  get doneLabel(): string {
    return this.nextSession ? this.$t("Done: {0}").toString().replace("{0}", this.nextSession.title) : this.$t("Done today").toString();
  }

  get noteBy(): string {
    return this.d.professionalName ? this.$t("{0} tells you").toString().replace("{0}", this.d.professionalName) : this.$t("Your professional tells you").toString();
  }

  timesPerWeek(n: number): string {
    if (n === 7) return this.$t("every day").toString();
    if (n === 1) return this.$t("once a week").toString();
    return this.$t("{0} days a week").toString().replace("{0}", String(n));
  }

  get openSession(): any {
    const asked = this.$route.query.session;
    if (!asked || this.d.sessions.length < 2) return null;
    if (asked === "next") return this.todaySession;
    return this.d.sessions.find((s: any) => s.id === Number(asked)) || null;
  }

  sessionLink(session: any): any {
    return { path: this.$route.path, query: Object.assign({}, this.$route.query, { session: String(session.id) }) };
  }

  sessionState(s: any): string {
    if (s === this.todaySession) return this.todayStatus === 0 ? this.$t("Done today").toString() : this.$t("Today it's your turn").toString();
    if (s.isNext) return this.$t("Next time").toString();
    if (s.isLastDone) return this.$t("The last one you did").toString();
    return "";
  }

  upload(path: string): string {
    return path ? store.state.settings.Uploads + "/" + path : "";
  }

  uploads(paths: string[]): string[] {
    return paths.map((p) => this.upload(p));
  }

  toggleDay(day: any) {
    if (day.status === null) return this.save(() => GuidelineApi.record(this.d.id, day.date, DayDone), () => this.toggleDay(day));
    return this.undo(day.date);
  }

  mark(status: number) {
    return this.save(() => GuidelineApi.record(this.d.id, this.today, status), () => this.mark(status));
  }

  undo(date: string) {
    return this.save(() => GuidelineApi.undo(this.d.id, date), () => this.undo(date));
  }

  retry() {
    const again = this.failed;
    this.error = "";
    this.failed = null;
    return again ? again() : Promise.resolve();
  }

  async save(call: () => Promise<any>, again: () => Promise<void>) {
    if (this.busy) return;
    this.busy = true;
    const res = await call();
    if (!res || res.ok === false) {
      this.busy = false;
      this.failed = again;
      this.error = res && res.errorMsg ? res.errorMsg : this.$t("It was not saved.").toString();
      return;
    }
    this.error = "";
    this.failed = null;
    await this.load();
    this.busy = false;
  }

  async load() {
    const data = await GuidelineApi.detail(this.$route.params.id, this.previewToken);
    this.d = data && data.ok ? data.model : null;
    this.loaded = true;
  }

  async mounted() {
    await this.load();
    if (!this.d) return;
    new SettingsService().setPageTitle(this.d.name);
  }
}
